import { createCustomElement } from '@angular/elements';
import { elementList } from '@app/elements/element-list';
import { lazyElementList } from '@app/elements/lazy-element-list';
import { registerLazyElements } from '@app/elements/lazy-element-loader';
import { createApplication } from '@angular/platform-browser';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@app/core/interceptors/auth-interceptor';
import { apiTokenInterceptor } from '@app/core/interceptors/api-token.interceptor';
import {
    provideZoneChangeDetection, DOCUMENT, importProvidersFrom,
    provideBrowserGlobalErrorListeners, enableProdMode
} from "@angular/core";
import { NgZone } from '@angular/core';

// This file only ever runs as the production build (angular.json swaps it in for main.ts under
// the "production" configuration) - without this, Angular's isDevMode() stays true forever, which
// cs-shared-lib's TranslateService gates on (see JOBS-2583 migration commit for translate.service.ts).
enableProdMode();
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MODULE_CONTEXT } from '@app/core/routing/constants';

import { provideRouter } from '@angular/router';
import { prodRoutes } from './app/app.routes';



import { MfeRegistryService } from '@app/shared/service-bridge/mfe-registry.service';
import {
    WINDOW_PROVIDERS,
    ChannelService,
    MicroserviceService,
    AppRootService,
    TranslateService,
    RegionalSettingsService,
    CountriesService,
    REGIONAL_SETTINGS_ENV_CONFIG,
    MICROSERVICE_ENV_CONFIG,
    HTTP_URL_ENV_CONFIG,
    UrlQueryBuilder,
} from 'cs-shared-lib';
import { environment } from '@environments/environment';

// UrlQueryBuilder is constructed via plain `new` (not Angular DI), so it can't take an injected
// config the way the services below do — these statics are its equivalent bootstrap wiring.
UrlQueryBuilder.production = environment.production;
UrlQueryBuilder.webserviceToken = environment.webserviceToken;

// Not yet consumed by anything — the local core/ services above read `environment` directly
// instead of injecting these. Provided in advance so migrating a local service onto its
// cs-shared-lib equivalent (which does inject its token) doesn't also require a main.prod.ts change.
const sharedLibEnvConfigProviders = [
    { provide: REGIONAL_SETTINGS_ENV_CONFIG, useValue: { production: environment.production } },
    {
        provide: MICROSERVICE_ENV_CONFIG,
        useValue: { availableEnvs: environment.availableEnvs, apiHost: environment.apiHost },
    },
    {
        provide: HTTP_URL_ENV_CONFIG,
        useValue: {
            production: environment.production,
            apiBasePath: environment.apiBasePath,
            microserviceApiReporting: environment.microserviceApiReporting,
            microserviceApiJob: environment.microserviceApiJob,
            microserviceApiV5Core: environment.microserviceApiV5Core,
            webserviceToken: environment.webserviceToken,
        },
    },
];

const commonProviders = [
    BrowserAnimationsModule,
    ...WINDOW_PROVIDERS,
    AppRootService,
    RegionalSettingsService,
    TranslateService,
    MicroserviceService,
    ChannelService,
    CountriesService,
    ...sharedLibEnvConfigProviders,

    provideHttpClient(
        withInterceptors([authInterceptor, apiTokenInterceptor])
    ),
    provideBrowserGlobalErrorListeners(),
    provideAnimations(),
    importProvidersFrom(ModalModule.forRoot()),
    importProvidersFrom(BsDropdownModule.forRoot()),
    { provide: DOCUMENT, useValue: document },
    { provide: MODULE_CONTEXT, useValue: 'Jobs' }
];


const prodProviders = [
    ...commonProviders,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(prodRoutes),
    {
        provide: NgZone,
        useValue: (window as any).ngZone
    },
];

createApplication({ providers: prodProviders })
    .then(appRef => {
        // Publish services to window using the app injector
        const registry = appRef.injector.get(MfeRegistryService);
        registry.publishServices();

        elementList.forEach((el) => {
            if (!customElements.get(el.tagName)) {
                const customEl = createCustomElement(el.class, {
                    injector: appRef.injector,
                });
                customElements.define(el.tagName, customEl);
            }
        });

        registerLazyElements(lazyElementList, appRef.injector);
    })
    .catch(err => console.error(err));
